/* eslint-disable react/prop-types */
import React, {
  MouseEvent, useEffect, useRef, useState,
} from 'react';
import {
  Form, Field, FormSpy, useFormState,
} from 'react-final-form';
import {
  Button,
  Heading,
  IconButton,
  Box,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useDisclosure,
  Flex,
  Switch,
  Portal,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { NIL, v4 } from 'uuid';
import {
  FaGripLines, FaInfoCircle, FaHistory, FaLock,
} from 'react-icons/fa';
import { CloseIcon } from '@chakra-ui/icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { QueryStatus } from 'react-query';
import useStateTaxRates from '../../hooks/useStateTaxRates';
import ExpandCollapseAllButton from '../ExpandCollapseAllButton'; // item 702 — standardized control
import useCompanyRnwy from '../../hooks/useCompanyRnwy'; // item 641 — RNWY standardized-language default
import { emptyPricing, emptyOption } from './rnwyPricing'; // item 656 — seed per-option FAQ defaults
import Alert from '../Alert';
import BrandIcon from '../BrandIcon';
import PageHeader from '../PageHeader';
import AuroraBackground from '../dashboard/AuroraBackground';
import { useAppearance } from '../../theme/AppearanceProvider';
import useGlassPanel from '../../theme/glassPanel';
import { useProjectMode } from '../../theme/ProjectModeProvider';
import useProject from '../../hooks/useProject';
import useProjects from '../../hooks/useProjects';
import FormBreadcrumbs, { Crumb } from '../FormBreadcrumbs';
import TextInputHidden from './TextInputHidden';
import FormActionBar from './FormActionBar';
import FormRow from './FormRow';
import deferFormChanges from './deferFormChanges'; // item 1314
import { DocumentItemsFormElementSharedProps, DocumentLineItemLike } from '../DocumentItemsFormElement';
import apiErrorMessage from '../../utils/apiError';
import useEditLock from '../../hooks/useEditLock';
import EditLockBanner from '../EditLockBanner';
import scrollContentToEl from '../../utils/scrollContent'; // item 1056

// The add/edit page shared by all five document types: header, Information/Items
// accordion, the whole category-names machinery, items state, submit with error
// toast, and the remove-category alert. Each per-type form supplies its hook
// result, its Information Section fields, and how its values map onto the model.

export interface DocumentFormConfig<TDoc, TItem> {
  title: string; // 'INVOICE'
  saveLabel: string; // 'Save Invoice'
  icon: string;
  iconWhite?: string;
  doc: TDoc | undefined;
  status: QueryStatus;
  tryUpdate: (doc: TDoc | undefined) => Promise<unknown>;
  validate: (values: TDoc) => ValidationErrors;
  getItems: (doc: TDoc) => TItem[];
  getCategoryNames: (doc: TDoc) => string;
  // mutate the submitted values with the scaffold-owned state before saving
  applyScaffoldState: (values: TDoc, state: { categoryNames: string, items: TItem[], id: string }) => void;
  ItemsElement: React.ComponentType<DocumentItemsFormElementSharedProps<TItem>>;
  // rendered inside the Information Section panel (FormRows with the per-type fields)
  infoSection: React.ReactNode;
  // optional content after the items grid, still inside the Items panel (e.g. global discount)
  afterItems?: React.ReactNode;
  // item 577 — the footer is three semantic clusters (see FormActionBar):
  // primary status verbs (Approve/Decline, Finalize…) — first and prominent
  footerPrimary?: React.ReactNode;
  // …then the utility tools (View PDF · Send · Sales tracking …), which wrap as a group
  footerActions?: React.ReactNode;
  // when true, hide the Save button (e.g. a finalized/locked invoice can't be saved — item)
  hideSave?: boolean;
  // item 874 (Chad) — HARD read-only: every input in the form body is natively disabled
  // (the item-594 fieldset trick) and a lock banner explains why + what to do instead.
  // The footer bar stays live (Void/PDF/Send must keep working on a locked document).
  readOnlyLock?: boolean;
  readOnlyNotice?: React.ReactNode;
  // item 797 (Chad) — a PURCHASE ORDER prompts to upload the client's original PO right
  // after the first save (we already have it). Flagged into nav state; the form opens the modal.
  promptClientPoOnCreate?: boolean;
  // item 1364 — extra nav state to hand the just-created doc's edit route (e.g. the WO form's
  // "email the PM?" prompt); computed from the saved values so the form can decide per doc
  createNavState?: (values: Record<string, unknown>) => Record<string, unknown> | undefined;
  // item 441 — the per-form terminal-state ribbon (DocumentStatusStamp); the FORM
  // decides the text/tone since only it knows its specific status — the scaffold
  // just renders it above the banners.
  statusStamp?: React.ReactNode;
  // item 233/288 — the first category name on a NEW document (editable). Defaults to
  // "Services"; PO + Sales Order pass "Equipment" (hardware/materials docs).
  defaultCategory?: string;
  // item 647 — Sch-B (RNWY/Crestron) proposals render as a numbered guided flow: the two
  // accordions become "Step 1" (info) / "Step 2" (items) glass step-cards, with a faint
  // "next step" hint under Step 1. OPT-IN and Sch-B-only — every other doc type (and the
  // Standard proposal) leaves this false and keeps the plain "Information Section" /
  // "Items Section" accordion headers untouched.
  stepMode?: boolean;
  // item 647 — RNWY guided flow ONLY: an ordered list of Information step-cards, rendered in place
  // of the single "Information Section" accordion. Each becomes its own collapsible glass step-card
  // titled by `title`. Standard proposals and every other doc type leave this undefined and keep
  // the single `infoSection` accordion untouched. Requires stepMode.
  infoSteps?: { title: string; content: React.ReactNode }[];
  // item 654 — RNWY guided flow ONLY: hide the standalone line-items GRID (RNWY is engine-priced,
  // with one-off items handled by the Step-2 "One-time add-ons"). Keeps `afterItems` (discount/tax)
  // and relabels that accordion "Pricing & tax". Standard proposals leave this false → full grid.
  hideItemsGrid?: boolean;
  // item 670 — RNWY guided flow ONLY: drop the trailing Items/"Pricing & tax" accordion ENTIRELY
  // (no line-items grid, no global discount, no sales tax). RNWY doesn't discount and adds tax at
  // invoice; its item settings live on a dedicated step instead.
  hideItemsSection?: boolean;
  // item 647 — RNWY guided flow ONLY: a progress indicator rendered in the STICKY page header
  // (so it stays visible while scrolling the steps). A render function receiving the live form
  // values — the scaffold lifts values out of the Form and calls this; the proposal owns the
  // per-step completion rules. Undefined for every other form.
  // item 710 (Chad) — the render fn also receives goToStep so the progress bar segments can be
  // clicked to open + scroll to that step.
  stepProgress?: (values: Record<string, unknown>, goToStep?: (index: number) => void) => React.ReactNode;
}

// item 647 — lifts the live form values OUT of the Form (where this is mounted) up to the scaffold
// so the sticky PageHeader — which renders ABOVE/outside the Form — can show the RNWY step-progress
// bar. Renders null; only mounted for the RNWY guided flow.
const HeaderValueLift: React.FC<{ onValues: (v: Record<string, unknown>) => void }> = ({ onValues }) => {
  const { values } = useFormState({ subscription: { values: true } });
  useEffect(() => { onValues(values as Record<string, unknown>); }, [values, onValues]);
  return null;
};

// item 232 — local-timezone yyyy-MM-dd (NOT toISOString, which is UTC and can shift a day
// near midnight). Used to default a new document's date to today.
const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// item 148 — explicit-Save model, hardened. We do NOT auto-save to the server;
// instead we (a) restore any local draft into the live form once it's ready and
// (b) warn before navigating away with unsaved changes. The draft itself is
// written to localStorage by the scaffold as the user types.

// Applies a restored draft's Information-Section values into the live form exactly
// once, AFTER the form has initialised from the saved doc — so the restored fields
// register as dirty (genuinely unsaved) rather than getting wiped by a late
// async re-initialisation when the document finishes loading.
const DraftValueRestorer: React.FC<{
  form: any;
  values: Record<string, unknown> | undefined;
  ready: boolean;
}> = ({ form, values, ready }) => {
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !ready || !values) return undefined;
    applied.current = true;
    // item 1314 — deferred one tick so no field swallows it as its 'first notification'
    return deferFormChanges(form, () => {
      Object.keys(values).forEach((k) => form.change(k, (values as any)[k]));
    });
  }, [form, values, ready]);
  return null;
};

// item 1517 — how long the localStorage draft write waits for the typing to pause
const DRAFT_WRITE_DEBOUNCE_MS = 400;

// item 194/557 — the save-status pill next to Save. ALWAYS visible on an editable form
// (Chad: people need to see "it's saved" to feel safe, not silence): on existing docs it
// narrates the auto-save lifecycle and rests at a green "All changes saved"; on NEW docs —
// where auto-save can't run yet (no id to save against) — it says plainly that nothing is
// saved until the first Save creates the document.
const AutoSaveBadge: React.FC<{
  state: 'clean' | 'dirty' | 'saving' | 'saved' | 'error';
  isNew: boolean;
}> = ({ state, isNew }) => {
  // item 958 — light-mode contrast: gray.400 read as ghost text on light glass; one step darker in light only
  const quiet = useColorModeValue('gray.500', 'gray.400');
  if (isNew) {
    const dirty = state === 'dirty';
    return (
      <Text fontSize="xs" color={dirty ? 'orange.400' : quiet} fontWeight="medium">
        {dirty ? 'Draft — not saved until you press Save' : 'Not saved yet — Save creates it'}
      </Text>
    );
  }
  const map = {
    clean: { t: 'All changes saved', c: 'green.400' },
    dirty: { t: 'Unsaved changes — auto-save runs when you pause', c: 'orange.400' },
    saving: { t: 'Auto-saving…', c: quiet },
    saved: { t: 'Auto-saved — all changes saved', c: 'green.400' },
    error: { t: 'Auto-save failed — use Save', c: 'red.400' },
  } as const;
  const m = map[state];
  return <Text fontSize="xs" color={m.c} fontWeight="medium">{m.t}</Text>;
};

// item 434 — sections render from the doc's category CSV, but items can carry
// categories the CSV doesn't list (the WO auto-created at approval never had one, so
// its copied items rendered NOWHERE — Chad's "empty" work order). Union them in so an
// item can never be invisible.
const withItemCategories = (cats: string, docItems: Array<{ category?: string }>): string => {
  // item 1280 (Chad: "why do we keep adding UNDEFINED categories") — the seed list is
  // cleaned first: blank entries dropped (a doc whose create-era save stored '' used to
  // seed the legacy 'UNDEFINED' sentinel next to the items' real categories).
  const list = cats.split(',').map((n) => n.trim()).filter(Boolean);
  const itemCats = new Set(docItems.map((i) => (i.category ?? '').trim()).filter(Boolean));
  itemCats.forEach((c) => { if (!list.includes(c)) list.push(c); });
  // the 'UNDEFINED' sentinel survives ONLY when items actually live in it, or when it's
  // the doc's sole category — an empty leftover beside real categories is the phantom.
  const cleaned = list.filter((n) => n.toUpperCase() !== 'UNDEFINED' || itemCats.has(n) || list.length === 1);
  return (cleaned.length > 0 ? cleaned : ['UNDEFINED']).join(',');
};

// dart (2026-07-14) — friendly names for the fields that can block a save, so a validation
// failure names what's missing instead of the Save button silently doing nothing.
const FIELD_LABELS: Record<string, string> = {
  proposalTitle: 'Name',
  serviceName: 'Name',
  projectId: 'Project',
  vendorId: 'Vendor',
  date: 'Date',
  platform: 'Platform',
  termsOption: 'Terms & Conditions',
  integratorSignatoryName: 'Signatory',
  retailEach: 'Price',
};
const humanizeField = (k: string): string => FIELD_LABELS[k]
  ?? k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();

const DocumentFormScaffold = <TDoc, TItem extends DocumentLineItemLike>(
  props: { config: DocumentFormConfig<TDoc, TItem> },
): React.ReactElement => {
  const { config } = props;
  const {
    title, saveLabel, icon, doc, tryUpdate, validate,
    getItems, getCategoryNames, applyScaffoldState, ItemsElement, infoSection, afterItems,
    footerPrimary, footerActions, hideSave, statusStamp, readOnlyLock, readOnlyNotice, defaultCategory = 'Services',
    stepMode, infoSteps, stepProgress, hideItemsGrid, hideItemsSection, promptClientPoOnCreate, createNavState,
  } = config;
  const { id } = useParams<{id: string}>();
  const history = useHistory();
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const [categoryNames, setCategoryNames] = useState('UNDEFINED');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [categoryEditMode, setCategoryEditMode] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [items, setItems] = useState<TItem[]>([]);
  const { aurora } = useAppearance();
  const glass = useGlassPanel(); // item 406 — the ONE glass material
  const draftBannerBg = useColorModeValue('orange.50', 'rgba(251,146,60,0.12)');
  const draftBannerBorder = useColorModeValue('orange.200', 'orange.700');
  // item 647 — Sch-B step-cards use THE canonical glass-panel material (skin-aware,
  // hairline whiteAlpha edge — never a hard/brand-colored border). Both the Step 1 and
  // Step 2 accordions share this treatment when stepMode is on.
  const glassPanel = useGlassPanel();
  const stepCardProps = stepMode ? {
    ...glassPanel, px: 4, pt: 2, pb: 3, mb: 4,
  } : {};
  // drop the brand-colored focus ring on the step headers (this tenant's brand is red)
  const stepBtnProps = stepMode ? { _focus: { boxShadow: 'none' } } : {};
  const infoFooterColor = useColorModeValue('gray.400', 'whiteAlpha.500');
  // item 668 — collapse/expand-ALL for the guided step-cards (stepMode only). Controlled index so a
  // single toggle opens or closes the whole group. Count = the info steps + the items/pricing card.
  const stepCount = (infoSteps?.length ?? 0) + (hideItemsSection ? 0 : 1);
  // item 709 (Chad) — a guided (automated) proposal OPENS fully collapsed at the top when the
  // "Auto-collapse steps" preference is on (default); Step 1 open otherwise. The item-539 mount
  // effect already scrolls the form to the top. (Key mirrors AUTO_COLLAPSE_KEY below.)
  const [openSteps, setOpenSteps] = useState<number[]>(() => {
    try {
      const s = localStorage.getItem('basix.rnwyAutoCollapse');
      return s === null || s === '1' ? [] : [0];
    } catch { return []; }
  });
  const allOpen = () => setOpenSteps(Array.from({ length: stepCount }, (_, k) => k));
  const collapseAll = () => setOpenSteps([]);
  // item 710 (Chad) — click a progress-bar segment to open that step and scroll it into view.
  // Single-open so the target is the only thing expanded; scrollMarginTop on each card keeps it
  // clear of the sticky header.
  const goToStep = React.useCallback((index: number) => {
    setOpenSteps([index]);
    requestAnimationFrame(() => {
      const el = document.getElementById(`rnwy-step-${index}`);
      scrollContentToEl(el); // item 1056 — scoped scroll (scrollIntoView shifted the locked shell)
    });
  }, []);
  // item 683 (Chad) — "Auto-collapse steps": a PERSONAL, persisted preference (localStorage, per
  // browser — same pattern as the Appearance skin). Default ON (Chad, 2026-07-16) so the long stepped
  // form opens tidy; an explicit OFF is remembered ('0'). The toggle at the top of the stepped form is
  // both the remembered default (persists across logins) AND the in-proposal override (flip it; it sticks).
  const AUTO_COLLAPSE_KEY = 'basix.rnwyAutoCollapse';
  const [autoCollapse, setAutoCollapse] = useState<boolean>(() => {
    // unset → default ON; otherwise honor the stored choice ('1' on / '0' off)
    try { const s = localStorage.getItem(AUTO_COLLAPSE_KEY); return s === null ? true : s === '1'; } catch { return true; }
  });
  const toggleAutoCollapse = (on: boolean) => {
    setAutoCollapse(on);
    try { localStorage.setItem(AUTO_COLLAPSE_KEY, on ? '1' : '0'); } catch { /* private mode — ignore */ }
    // turning it ON takes effect immediately: keep only the last-opened step open
    if (on) setOpenSteps((prev) => (prev.length ? [prev[prev.length - 1]] : [0]));
  };
  // item 647 — the items accordion's title: "Items" in the RNWY guided flow, "Step 2" in the old
  // 2-step mode, "Items Section" everywhere else.
  let itemsSectionTitle = 'Items Section';
  if (hideItemsGrid) itemsSectionTitle = 'Pricing & tax'; // item 654 — RNWY: no line-items grid
  else if (infoSteps?.length) itemsSectionTitle = 'Items';
  else if (stepMode) itemsSectionTitle = 'Step 2';
  // item 647 — live form values lifted up so the sticky header can render the RNWY progress bar
  const [liveValues, setLiveValues] = useState<Record<string, unknown>>({});
  const toast = useToast();

  // item 213 — pessimistic edit-lock keyed by document type + id. While another user holds
  // it, `lockedByOther` is true → the form shows the banner and Save is disabled until the
  // viewer takes over. No-op on a new/unsaved doc.
  const { readOnly: lockedByOther, holderName: lockHolder, takeOver: lockTakeOver } = useEditLock(title, id);

  // item 148 — local draft + unsaved-changes guard. Disabled on locked docs
  // (hideSave) since they can't be saved — a guard there would only nag.
  const draftEnabled = !hideSave;
  const isNewDoc = id === undefined || id === NIL;
  // item 194 — debounced auto-save for EXISTING documents only (new/unsaved docs keep
  // explicit Save + the localStorage draft until they have an id; locked/finalized docs
  // never auto-save). Keeps the explicit Save as the official commit; auto-save fills gaps.
  const autoSaveEnabled = !hideSave && !isNewDoc;
  // item 553 — the localStorage draft (the "restored unsaved changes from your last visit"
  // banner) is ONLY for NEW docs, which have no auto-save. EXISTING docs auto-save every
  // change to the server, so a local draft there was pure redundancy — it resurfaced as a
  // confusing banner on an edit and, via the shared ":new"-style scoping, felt like it
  // "carried" from one document to another (Chad). New docs keep it as their only safety net.
  const localDraftEnabled = draftEnabled && isNewDoc;
  // item 210/816 — prefill the company default tax state whenever the doc has NONE (new OR a
  // saved doc that never got one, e.g. a converted Sales Order). Chad: the default should
  // always be there without manual entry; change it per-doc only when it differs. Proposal /
  // Work Order / Invoice carry `taxState`; harmless on PO/Bill, which ignore it.
  const { defaultState } = useStateTaxRates();
  const docTaxState = (doc as { taxState?: string } | undefined)?.taxState;
  const taxStatePrefill = !docTaxState ? { taxState: defaultState } : {};
  // item 544 — a NEW document auto-selects its project ONLY when the add came FROM a project
  // (the project's grid passes ?projectId=<id> in the URL). A DIRECT add from a Basix Six tab
  // carries no param → the project field stays empty and is required, so you must choose one
  // (Chad: it must never silently pick a random/lingering project). Replaces the old prefill
  // from a lingering activeProject, which auto-filled the last project you'd viewed.
  const { activeProject, enterProject } = useProjectMode();
  const { search } = useLocation();
  const { rnwy } = useCompanyRnwy(); // item 641 — the RNWY standardized-language default to seed
  const queryProjectId = (() => {
    const v = new URLSearchParams(search).get('projectId');
    return v && v !== NIL ? v : undefined;
  })();
  const docProjectId = (doc as { projectId?: string } | undefined)?.projectId;
  const projectPrefill = isNewDoc && !docProjectId && queryProjectId ? { projectId: queryProjectId } : {};
  // item 232 — default a NEW document's date to today (editable). All doc types carry `date`.
  const docDate = (doc as { date?: string } | undefined)?.date;
  const datePrefill = isNewDoc && !docDate ? { date: todayStr() } : {};

  // item 417 — breadcrumb trail back out of a deep edit: Projects → project → doc when
  // the doc belongs to a project, else the type's grid → doc. Project name comes from
  // project mode when we're inside it (free), else the cached project query.
  const LIST_ROUTES: Record<string, string> = {
    INVOICE: '/Invoices',
    PROPOSAL: '/proposals',
    'WORK ORDER': '/WorkOrders',
    'SALES ORDER': '/WorkOrders',
    'PURCHASE ORDER': '/PurchaseOrder',
    BILL: '/bills',
  };
  // item 795 (Chad) — the per-doc EDIT route base, so a brand-new save lands ON the created
  // doc (keeps you in it — the item-534 preference — and puts the Received-PO upload right
  // there instead of bouncing to the project to go hunt for the PO you just made).
  const EDIT_ROUTES: Record<string, string> = {
    INVOICE: '/invoice',
    PROPOSAL: '/proposal',
    'WORK ORDER': '/WorkOrder',
    'SALES ORDER': '/WorkOrder',
    'PURCHASE ORDER': '/PurchaseOrder',
    BILL: '/bill',
  };
  // item 553 — keep you IN the project while editing its docs: the breadcrumb + Back use the
  // doc's own project, else the ?projectId= from an add, else the sticky project context you
  // came from (so editing from a project returns you there, not to the Basix Six list). This is
  // navigation only — it does NOT prefill the doc's project field (that stays item-544 strict).
  const rawCrumbProjectId = docProjectId ?? (isNewDoc ? queryProjectId : undefined) ?? activeProject?.id;
  const crumbProjectId = rawCrumbProjectId && rawCrumbProjectId !== NIL ? rawCrumbProjectId : undefined;
  const { data: crumbProject } = useProject({ id: crumbProjectId ?? NIL });
  // dart (2026-07-14) — keep the top project-context bar in sync with the DOC's project (the
  // breadcrumb already follows it; the bar read the stale sticky project). When the doc's project
  // resolves — or CHANGES, e.g. a proposal moved — enter it so the bar + breadcrumb always agree.
  useEffect(() => {
    if (crumbProjectId && crumbProject?.name) {
      enterProject({ id: crumbProjectId, name: crumbProject.name });
    }
  }, [crumbProjectId, crumbProject?.name, enterProject]);
  // item 527 — the customer to auto-fill comes from the lightweight PROJECTS LIST (already
  // loaded for the sidebar; every row carries customerId), NOT the heavy project-detail query
  // — so the fill is instant and can't be defeated by that query being slow/empty.
  const { data: scaffoldProjects } = useProjects();
  const prefillCustomerId = (scaffoldProjects?.index ?? [])
    .find((p) => p.id === crumbProjectId)?.customerId;
  // item 527 — prefill through initialValues (like taxState/project/date above) so it SURVIVES
  // the form re-initialising when async prefills load; setting it via an effect got wiped.
  const docCustomerId = (doc as { customerId?: string } | undefined)?.customerId;
  const customerPrefill = isNewDoc && !docCustomerId && prefillCustomerId
    ? { customerId: prefillCustomerId } : {};
  // item 635 — the RNWY 2.0 fork template (?kind=) MUST ride in through initialValues, NOT a
  // post-mount form.change: the async-prefill re-initialise wipes effect-set fields (see item 527),
  // which left a picked RNWY/Crestron proposal opening in Standard mode (no RNWY section). Seeding
  // kind + isRnwy here makes the fork choice stick — for display AND for what saves.
  const urlKind = new URLSearchParams(search).get('kind');
  const kindPrefill = isNewDoc && urlKind
    ? {
      kind: ({ rnwy: 1, crestron: 2 } as Record<string, number>)[urlKind] ?? 0,
      isRnwy: urlKind === 'rnwy',
      // item 640 — RNWY proposals lock to T&C Option 2 (RNWY Pre-Contract terms). This was
      // done by RnwyTermsSync on the OFF→ON toggle, but item 635 now sets isRnwy from the
      // start (no transition) so the sync never fired; seed it here (survives re-init, editable).
      // item 641 — seed the RNWY Standardized Language from the Settings default the SAME way (the
      // effect-based fill was wiped on first open → blank until reload). Only when it has loaded;
      // once it does, the re-init adopts it into the untouched field.
      ...(urlKind === 'rnwy'
        ? {
          termsOption: 2,
          ...(rnwy?.standardLanguage ? { rnwyStandardLanguage: rnwy.standardLanguage } : {}),
          // item 656 — seed the three license-option FAQs from the Settings → RNWY defaults, the
          // same re-init-safe way (survives async prefill; untouched fields adopt it). Stored in
          // rnwyPricingJson per-option .faq; the recap cards + PDF read it.
          ...(rnwy?.faqPerpetual || rnwy?.faqTwoYear || rnwy?.faqFiveYear
            ? {
              rnwyPricingJson: JSON.stringify({
                ...emptyPricing(),
                perpetual: { ...emptyOption(), faq: rnwy.faqPerpetual ?? '' },
                twoYear: { ...emptyOption(), faq: rnwy.faqTwoYear ?? '' },
                fiveYear: { ...emptyOption(), faq: rnwy.faqFiveYear ?? '' },
              }),
            }
            : {}),
        }
        : {}),
    }
    : {};
  const niceTitle = title.toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase());
  const crumbProjectName = (crumbProjectId && crumbProjectId === activeProject?.id ? activeProject?.name : undefined)
    ?? crumbProject?.name;
  // item 1055/1058 (Chad) — say WHICH proposal/invoice/PO you're inside: the service-address
  // friendly name rides the crumb in parens ("Proposal (Center Bar)"). Per item 465 the name
  // IS the title field on proposals/work orders (proposalTitle/workOrderTitle); invoices/POs/
  // bills carry the dedicated serviceName.
  const crumbServiceName = String(
    (doc as any)?.serviceName ?? (doc as any)?.proposalTitle ?? (doc as any)?.workOrderTitle ?? '',
  ).trim();
  const hereLabel = `${isNewDoc ? 'New ' : ''}${niceTitle}${crumbServiceName ? ` (${crumbServiceName})` : ''}`;
  const crumbs: Crumb[] = crumbProjectId
    ? [
      { label: 'Projects', to: '/' },
      { label: crumbProjectName ?? 'Project', to: `/project/${crumbProjectId}` },
      { label: hereLabel },
    ]
    : [
      { label: `${niceTitle}s`, to: LIST_ROUTES[title] ?? '/' },
      { label: hereLabel },
    ];
  // item 533 — DETERMINISTIC close (X / Cancel): return to the doc's project, else the type's
  // grid, else back. history.goBack() went "wherever you came from" — if you'd been on Users
  // it reopened Users instead of closing (Chad). Mirrors the save-return in onSubmit.
  const closeForm = () => {
    if (crumbProjectId) history.push(`/project/${crumbProjectId}`);
    else if (LIST_ROUTES[title]) history.push(LIST_ROUTES[title]);
    else history.goBack();
  };
  const draftKey = `basix:draft:${title}:${id || 'new'}`;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [restoredValues, setRestoredValues] = useState<Record<string, unknown> | undefined>(undefined);
  const dirtyRef = useRef(false);
  const bypassGuardRef = useRef(false);
  const baselineReadyRef = useRef(false);
  const baselineItemsRef = useRef('[]');
  const baselineCatsRef = useRef('UNDEFINED');
  const latestValuesRef = useRef<Record<string, unknown>>({});
  // item 194 — auto-save bookkeeping
  const [saveBadge, setSaveBadge] = useState<'clean' | 'dirty' | 'saving' | 'saved' | 'error'>('clean');
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSavingRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const pendingChangesRef = useRef(false);
  const skipBaselineResetRef = useRef(false);
  // item 537 — the currently-running save (auto OR explicit). The WO/etc. entities carry a
  // RowVersion concurrency token, so TWO overlapping saves race → the 2nd hits a stale
  // RowVersion → DbUpdateConcurrencyException (409). Serialize through this promise so a
  // save always waits for the one before it; the explicit Save also cancels any pending
  // auto-save timer first. (Regressed when Save stopped navigating away — item 534 — which
  // used to unmount the form and cancel the armed auto-save.)
  const inFlightSaveRef = useRef<Promise<unknown> | null>(null);
  // item 1353b — auto-save fires only on REAL user input. A post-save refetch reinitializes the
  // form (react-final-form fires FormSpy), the editor normalizes values, the server echoes a
  // field back differently (a dropped project change, null vs '') — none of that is a user edit,
  // so none of it may arm the timer. Document-level capture listeners flip this; a dispatched
  // save clears it. This is what makes the runaway structurally impossible.
  const userActivityRef = useRef(false);
  // item 1353b — belt AND braces: a rolling counter pauses auto-save outright if it still fires
  // absurdly often (a loop = ~20/min; a human pausing mid-typing is nowhere near 12/min).
  const autoSaveTimesRef = useRef<number[]>([]);
  const autoSavePausedRef = useRef(false);
  useEffect(() => {
    const mark = () => { userActivityRef.current = true; };
    const evts = ['keydown', 'input', 'change', 'pointerdown', 'paste', 'drop'];
    evts.forEach((e) => document.addEventListener(e, mark, true));
    return () => evts.forEach((e) => document.removeEventListener(e, mark, true));
  }, []);

  // item 539 — opening a document form starts you at the TOP of it. ADD navigates here
  // from wherever you were scrolled in the project view; without this you'd land mid-page.
  // Scrolls the app's single content-scroll region (SidebarWithHeader) to the top.
  useEffect(() => {
    const el = document.getElementById('basix-content-scroll');
    if (el) el.scrollTo({ top: 0 }); else window.scrollTo(0, 0);
  }, [id]);

  // Write the current form + items + categories to localStorage when they diverge
  // from the saved document; clear it when they match again (or the user reverts).
  // item 1517 (perf) — the WRITE (a JSON.stringify of every field + every line item) used to run
  // on EVERY keystroke via the FormSpy below; it is debounced 400 ms now. The badge flip stays
  // synchronous (it's free), and a pending write is FLUSHED on beforeunload, on the existing
  // 'basix:flush-autosave' event and on unmount, so nothing typed in the last 400 ms is lost.
  const draftWriteTimer = useRef<number | null>(null);
  const writeDraft = () => {
    if (!localDraftEnabled || !baselineReadyRef.current) return;
    const itemsDirty = JSON.stringify(items) !== baselineItemsRef.current;
    const catsDirty = categoryNames !== baselineCatsRef.current;
    const dirty = dirtyRef.current || itemsDirty || catsDirty;
    try {
      if (dirty) {
        window.localStorage.setItem(draftKey, JSON.stringify({
          values: latestValuesRef.current,
          items,
          categoryNames,
        }));
      } else {
        window.localStorage.removeItem(draftKey);
      }
    } catch {
      /* ignore quota / private-mode errors — drafts are best-effort */
    }
  };
  // always the LATEST closure (current items/categories) when a flush fires
  const writeDraftRef = useRef(writeDraft);
  writeDraftRef.current = writeDraft;
  const flushDraft = () => {
    if (draftWriteTimer.current === null) return; // nothing pending
    window.clearTimeout(draftWriteTimer.current);
    draftWriteTimer.current = null;
    writeDraftRef.current();
  };
  const flushDraftRef = useRef(flushDraft);
  flushDraftRef.current = flushDraft;
  const persistDraft = () => {
    if (!localDraftEnabled || !baselineReadyRef.current) return;
    const itemsDirty = JSON.stringify(items) !== baselineItemsRef.current;
    const catsDirty = categoryNames !== baselineCatsRef.current;
    const dirty = dirtyRef.current || itemsDirty || catsDirty;
    // item 557 — the NEW-doc badge (auto-save is off there): flip Draft/Not-saved-yet
    setSaveBadge(dirty ? 'dirty' : 'clean');
    if (draftWriteTimer.current !== null) window.clearTimeout(draftWriteTimer.current);
    draftWriteTimer.current = window.setTimeout(() => {
      draftWriteTimer.current = null;
      writeDraftRef.current();
    }, DRAFT_WRITE_DEBOUNCE_MS);
  };
  useEffect(() => {
    const flush = () => flushDraftRef.current();
    window.addEventListener('beforeunload', flush);
    window.addEventListener('basix:flush-autosave', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('basix:flush-autosave', flush);
      flush(); // in-app navigation unmounts the form — the draft banner must find the last keystrokes
    };
  }, []);

  // item 194 — a JSON snapshot of everything that gets saved (form values + items +
  // categories), used to dedupe redundant auto-saves.
  // item 1352d (Tom's runaway: a save every ~3s, 5k+ requests) — VOLATILE server-audit
  // fields are EXCLUDED: every save bumps lastModifiedDate, so the refetched doc made each
  // snapshot look new; combined with any perpetually-dirty field (the 1352b null-vs-''
  // markdown clear) the form saved in an infinite loop. Ignoring audit noise makes the
  // dedupe converge no matter what.
  // item 1353b — the strip is now DEEP (every save also bumps each LINE's audit stamps inside
  // the values' item arrays, which the top-level strip missed) and EMPTY-CANONICAL (null,
  // undefined and '' are the same "nothing" — the editor and the server disagree on which one
  // an empty field is, and that disagreement was a perpetual diff).
  const VOLATILE_KEYS = new Set(['lastModifiedDate', 'lastModifiedById', 'lastModifiedBy',
    'createdDate', 'createdBy', 'createdById', 'rowVersion']);
  const canonical = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canonical);
    if (v && typeof v === 'object') {
      const src = v as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      Object.keys(src).sort().forEach((k) => {
        if (VOLATILE_KEYS.has(k)) return;
        const c = canonical(src[k]);
        if (c === null || c === undefined || c === '') return;
        out[k] = c;
      });
      return out;
    }
    return v;
  };
  const autoSaveSnapshot = () => JSON.stringify(canonical({ values: latestValuesRef.current ?? {}, items, categoryNames }));

  // Push the current (valid) state to the server WITHOUT navigating away — the
  // explicit Save stays the official commit; this just fills the gaps as you edit.
  const runAutoSave = async () => {
    if (!autoSaveEnabled || autoSavingRef.current || lockedByOther || autoSavePausedRef.current) return;
    const snapshot = autoSaveSnapshot();
    if (snapshot === lastSavedSnapshotRef.current) { setSaveBadge('saved'); return; }
    const payload = { ...latestValuesRef.current } as unknown as TDoc;
    // only auto-save a VALID document — invalid fields wait for the user to fix them
    const errors = validate(payload);
    if (errors && Object.keys(errors).length > 0) { setSaveBadge('dirty'); return; }
    // item 1353b — circuit breaker: 12+ auto-saves inside a minute is not a human editing
    const now = Date.now();
    autoSaveTimesRef.current = autoSaveTimesRef.current.filter((t) => now - t < 60_000).concat(now);
    if (autoSaveTimesRef.current.length > 12) {
      autoSavePausedRef.current = true;
      setSaveBadge('error');
      toast.close('autosave-paused');
      toast({
        id: 'autosave-paused',
        title: 'Auto-save paused',
        description: 'This form kept re-saving on its own — press Save to commit your changes (that also resumes auto-save).',
        status: 'warning',
        position: 'bottom-right',
        duration: 12000,
        isClosable: true,
      });
      return;
    }
    userActivityRef.current = false; // this save consumes the user's activity
    applyScaffoldState(payload, { categoryNames, items, id: id === undefined ? NIL : id });
    autoSavingRef.current = true;
    setSaveBadge('saving');
    // the post-save detail refetch must NOT clobber in-flight edits — tell the baseline
    // effect to re-baseline silently instead of resetting items/categories.
    skipBaselineResetRef.current = true;
    const work = (async () => {
      try {
        await tryUpdate(payload);
        lastSavedSnapshotRef.current = snapshot;
        pendingChangesRef.current = false;
        try { window.localStorage.removeItem(draftKey); } catch { /* best-effort */ }
        setSaveBadge('saved');
        // dart (2026-07-16, Chad) — a slide-in "Auto-save complete" toast (bottom-right) so you SEE
        // the save fire wherever you are on the form (the status pill is pinned by the Save button,
        // off-screen on a long form). Same toast id → it re-slides instead of stacking.
        toast.close('autosave-complete');
        toast({
          id: 'autosave-complete',
          title: 'Auto-save complete',
          status: 'success',
          position: 'bottom-right',
          duration: 2200,
          isClosable: true,
        });
      } catch (e: any) {
        skipBaselineResetRef.current = false;
        setSaveBadge('error');
        // item 1353d — a REFUSED auto-save used to fail silently (badge only). Chad changed a WO's
        // project, the server refused, and nothing said so: "it does not work". Surface the
        // server's reason once per distinct message.
        const reason = e?.response?.data;
        if (typeof reason === 'string' && reason.trim()) {
          toast.close('autosave-refused');
          toast({
            id: 'autosave-refused',
            title: 'Not saved',
            description: reason,
            status: 'error',
            position: 'bottom-right',
            duration: 9000,
            isClosable: true,
          });
        }
      } finally {
        autoSavingRef.current = false;
      }
    })();
    inFlightSaveRef.current = work;
    try { await work; } finally { if (inFlightSaveRef.current === work) inFlightSaveRef.current = null; }
  };

  // Debounce: reschedule on every change; save 1.5s after the user pauses typing.
  const scheduleAutoSave = () => {
    if (!autoSaveEnabled || !baselineReadyRef.current) return;
    const itemsDirty = JSON.stringify(items) !== baselineItemsRef.current;
    const catsDirty = categoryNames !== baselineCatsRef.current;
    const dirty = dirtyRef.current || itemsDirty || catsDirty;
    const snapshot = autoSaveSnapshot();
    pendingChangesRef.current = dirty && snapshot !== lastSavedSnapshotRef.current;
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    if (!pendingChangesRef.current) return;
    setSaveBadge('dirty');
    // item 1353b — no user input since the last save = a programmatic change (refetch echo,
    // reinitialize, editor normalization). Never arm on those.
    if (!userActivityRef.current || autoSavePausedRef.current) return;
    autoSaveTimer.current = setTimeout(() => { runAutoSave(); }, 1500);
  };

  // item 841d (Chad) — flush a pending autosave ON DEMAND, so a Preview/Download PDF reflects the
  // very latest edits (e.g. a just-typed Help Desk tier). Preview used to open the SAVED state while
  // autosave was still debouncing (1.5s) → the edit "didn't show" until another change forced a save.
  // ViewPdfButton dispatches 'basix:flush-autosave' and waits for 'basix:autosave-flushed'.
  const runAutoSaveRef = useRef(runAutoSave);
  runAutoSaveRef.current = runAutoSave;
  useEffect(() => {
    const handler = () => {
      Promise.resolve(runAutoSaveRef.current())
        .catch(() => { /* fall through — the fallback timeout still opens the PDF */ })
        .finally(() => window.dispatchEvent(new Event('basix:autosave-flushed')));
    };
    window.addEventListener('basix:flush-autosave', handler);
    return () => window.removeEventListener('basix:flush-autosave', handler);
  }, []);

  // Read any saved draft once on mount, before the document loads, so the
  // baseline effect knows whether to populate from the doc or keep the draft.
  useEffect(() => {
    if (localDraftEnabled) {
      try {
        const raw = window.localStorage.getItem(draftKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            if (parsed.values && typeof parsed.values === 'object') setRestoredValues(parsed.values);
            if (Array.isArray(parsed.items)) setItems(parsed.items);
            if (typeof parsed.categoryNames === 'string') setCategoryNames(parsed.categoryNames);
            setDraftRestored(true);
          }
        }
      } catch {
        /* corrupt draft — ignore it */
      }
    }
    setDraftLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // item 553 — a NEW-doc draft belongs to THIS editing session only: clear it on unmount so
  // it can never resurface on the NEXT new document (new docs share a ":new" key). A genuine
  // crash/refresh doesn't unmount, so real recovery within the same session still works.
  useEffect(() => () => {
    if (!localDraftEnabled) return;
    try { window.localStorage.removeItem(draftKeyRef.current); } catch { /* best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Establish the saved-document baseline (what "dirty" is measured against) and,
  // when there is no draft to restore, populate the live items/categories from it.
  useEffect(() => {
    if (!draftLoaded) return;
    if (!isNewDoc && doc === undefined) return; // editing: wait for the doc to load
    // item 194 — a post-auto-save refetch: advance the baseline to the just-saved
    // state but DON'T overwrite the live items/categories (the user may have kept
    // typing during the save).
    if (skipBaselineResetRef.current) {
      skipBaselineResetRef.current = false;
      baselineItemsRef.current = JSON.stringify(items);
      baselineCatsRef.current = categoryNames;
      return;
    }
    const baseItems = (!isNewDoc && doc) ? (getItems(doc) || []) : [];
    // item 233 — a NEW document starts with a first category named "Services" (editable),
    // instead of the bare "UNDEFINED" sentinel. Existing docs keep their saved categories.
    const baseCats = (!isNewDoc && doc)
      ? withItemCategories(getCategoryNames(doc) || 'UNDEFINED', baseItems as Array<{ category?: string }>)
      : defaultCategory;
    // item 555 — SAVE-RACE FIX. The FIRST load must populate the grid; but a LATER doc refetch
    // (a background react-query refresh, or a save round-trip that isn't the exact one the
    // one-shot skip guard caught) must NOT overwrite edits the user is still typing. Adopt the
    // incoming server state ONLY when the grid is CLEAN (still equals the current baseline); when
    // it's dirty, keep the user's edits and just advance the baseline so "dirty" stays measured
    // against the latest server state. This is what was reverting group prices / category names.
    const firstLoad = !baselineReadyRef.current;
    const gridDirty = JSON.stringify(items) !== baselineItemsRef.current
      || categoryNames !== baselineCatsRef.current;
    baselineItemsRef.current = JSON.stringify(baseItems);
    baselineCatsRef.current = baseCats;
    if (!draftRestored && (firstLoad || !gridDirty)) {
      setItems(baseItems as TItem[]);
      setCategoryNames(baseCats);
    }
    baselineReadyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, draftLoaded, draftRestored]);

  // item 1246 — the default first category FOLLOWS the document type (Work Order = Services,
  // Sales Order = Equipment; the form passes defaultCategory live). When the prop changes and
  // the grid still holds EXACTLY the old default (one category, never renamed/added-to by the
  // user), rename it in place — flipping the Document Type must never strand a wrong default
  // or leave a second phantom category next to the one the user defines. Any user-touched
  // structure (renamed, or extra categories) is left strictly alone.
  const prevDefaultCatRef = useRef(defaultCategory);
  useEffect(() => {
    const prev = prevDefaultCatRef.current;
    if (prev === defaultCategory) return;
    prevDefaultCatRef.current = defaultCategory;
    if (categoryNames !== prev) return;
    setCategoryNames(defaultCategory);
    setItems((cur) => cur.map((i) => (i.category === prev ? { ...i, category: defaultCategory } : i)));
    setActiveCategory((cur) => (cur === prev ? defaultCategory : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCategory]);

  // Persist whenever the items grid or categories change (form-field changes are
  // caught by the FormSpy below).
  useEffect(() => {
    persistDraft();
    scheduleAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, categoryNames]);

  // item 194 — cancel any pending auto-save on unmount
  useEffect(() => () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
  }, []);

  // item 647 (Chad) — the in-app route-leave CONFIRM was REMOVED. Every trip to a tool/settings
  // surface (which are routes, not true overlays) tripped a "you'll lose your changes" prompt —
  // but the localStorage DRAFT below already preserves unsaved work across navigation, so the
  // confirm was misleading noise. We keep ONLY the browser-unload guard (hard tab close / refresh)
  // as a last-resort safety net; in-app navigation now flows freely and your edits are restored
  // when you return (the draft banner tells you so).
  // dart (2026-07-14) — the native beforeunload "Leave site?" prompt was REMOVED (Chad: no browser
  // confirmations — they get popup-blocked and interrupt saving/progress). Unsaved work is already
  // protected by auto-save + the draft-restore banner, so the native dialog was a redundant nag.

  const clearDraft = () => {
    try {
      window.localStorage.removeItem(draftKey);
    } catch {
      /* ignore */
    }
  };

  // item 439 — a FINALIZED/locked document can't be saved, so a leftover local
  // draft is pure noise that reads like pending edits (Chad: a just-finalized
  // invoice still offered "restored unsaved changes"). The mount-time draft read
  // runs before the doc (and its locked state) loads — so once the doc arrives
  // locked, purge the draft and fall back to the saved document.
  useEffect(() => {
    if (!draftLoaded || !hideSave) return;
    clearDraft();
    if (draftRestored) {
      setRestoredValues(undefined);
      setDraftRestored(false);
      if (!isNewDoc && doc) {
        const docItems = getItems(doc) || [];
        setItems(docItems);
        setCategoryNames(withItemCategories(getCategoryNames(doc) || 'UNDEFINED', docItems as Array<{ category?: string }>));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLoaded, hideSave, doc]);

  const discardDraft = (form: any) => {
    clearDraft();
    setRestoredValues(undefined);
    setDraftRestored(false);
    if (!isNewDoc && doc) {
      const docItems = getItems(doc) || [];
      setItems(docItems);
      setCategoryNames(withItemCategories(getCategoryNames(doc) || 'UNDEFINED', docItems as Array<{ category?: string }>));
    } else {
      setItems([]);
      setCategoryNames('UNDEFINED');
    }
    form.reset();
  };

  let alertCatOpen: any;
  const onSubmit = async (values: TDoc) => {
    // item 213 — refuse to save over another user who holds the edit lock
    if (lockedByOther) {
      toast({
        title: 'Locked',
        description: `${lockHolder || 'Another user'} is editing this. Take over before saving.`,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    applyScaffoldState(values, { categoryNames, items, id: id === undefined ? NIL : id });
    // item 537 — serialize against auto-save: cancel any pending auto-save, wait out one
    // already in flight, and hold autoSavingRef so a timer firing mid-save bails. Prevents
    // two overlapping writes racing on the RowVersion token (409 concurrency error).
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    if (inFlightSaveRef.current) { try { await inFlightSaveRef.current; } catch { /* its own toast/badge */ } }
    autoSavingRef.current = true;
    const snapshot = autoSaveSnapshot(); // what we're committing, for the saved-baseline
    try {
      setSaveBadge('saving');
      // don't let the post-save detail refetch clobber the edits still on screen — the
      // baseline effect re-baselines silently (same mechanism auto-save uses).
      if (!isNewDoc) skipBaselineResetRef.current = true;
      const savedId = await tryUpdate(values);
      clearDraft();
      // item 795 (Chad) — a brand-new doc now LANDS ON ITSELF: the create endpoint returns the
      // new id (threaded through useDocument), so instead of bouncing to the project we open the
      // created doc's edit route. You stay in the doc you just made (the item-534 preference) and
      // the Received-PO upload is right there. Falls back to the project only if no id/route.
      if (isNewDoc) {
        bypassGuardRef.current = true; // navigating after a successful create — don't nag
        const editBase = EDIT_ROUTES[title];
        if (editBase && typeof savedId === 'string' && savedId && savedId !== NIL) {
          // item 797 (Chad) — a PURCHASE ORDER prompts to book the client's PO right after
          // create (we already have it in hand). Flag it via nav state so the re-mounted form
          // opens the upload modal once.
          const navState = {
            ...(promptClientPoOnCreate ? { promptClientPo: true } : {}),
            ...(createNavState?.(values as unknown as Record<string, unknown>) ?? {}), // item 1364
          };
          history.push(`${editBase}/${savedId}`, Object.keys(navState).length ? navState : undefined);
          return;
        }
        const savedProjectId = (values as { projectId?: string })?.projectId ?? docProjectId;
        if (savedProjectId && savedProjectId !== NIL) history.push(`/project/${savedProjectId}`);
        else history.goBack();
        return;
      }
      // item 534 — STAY in the form on save (Chad: "we can't hit save and then move on").
      // Mark everything saved so the Save button grays out; it re-enables the moment you
      // edit again. No navigation — you keep working in the doc you were in.
      lastSavedSnapshotRef.current = snapshot;
      pendingChangesRef.current = false;
      autoSavePausedRef.current = false; // item 1353b — an explicit Save resumes auto-save
      autoSaveTimesRef.current = [];
      userActivityRef.current = false;
      setSaveBadge('saved');
    } catch (e) {
      skipBaselineResetRef.current = false;
      setSaveBadge('error');
      toast({
        title: 'Save failed',
        description: apiErrorMessage(e, 'Your changes were NOT saved. Please try again.'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      // item 537 — release the serialize guard so later edits can auto-save again
      autoSavingRef.current = false;
    }
  };

  const catReplace = (oldName: string, newName: string) => {
    // plain split/join — the old code built a RegExp from raw user input, so
    // names containing ( ) , * etc. corrupted the whole category list
    const names = categoryNames.split(',');
    const updated = newName.length > 0
      ? names.map((n) => (n === oldName ? newName : n))
      : names.filter((n) => n !== oldName);
    setCategoryNames(updated.join(','));
  };

  const catItemsUpdateCategoryName = (oldName: string, newName: string) => {
    // now we have to fix any items that were in that category
    const newItems = [...items];
    newItems.forEach(
      // eslint-disable-next-line max-len,no-return-assign
      (i) => i.category = (i.category || '').toUpperCase() === activeCategory.toUpperCase() ? newName : i.category,
    );
    setItems(newItems);
  };

  // item 553 — categories are identified by NAME across this whole editor (items group by
  // category name; REMOVE/RENAME/MOVE match by name). Two categories with the SAME name
  // therefore collide catastrophically: adding an item to one shows it in BOTH, and removing
  // one removes EVERY category with that name (Chad's data-loss bug). So every new/duplicated
  // category MUST get a name that isn't already in use — append " 2", " 3", … until unique.
  const uniqueCategoryName = (base: string, exclude?: string): string => {
    const skip = exclude?.trim().toUpperCase();
    const existing = categoryNames.split(',')
      .map((n) => n.trim().toUpperCase())
      .filter((n) => !skip || n !== skip);
    if (!existing.includes(base.trim().toUpperCase())) return base;
    let n = 2;
    while (existing.includes(`${base} ${n}`.toUpperCase())) n += 1;
    return `${base} ${n}`;
  };

  const categoryAdd = () => {
    const newName = uniqueCategoryName('New Category');
    setCategoryNames(`${categoryNames},${newName}`);
  };

  const duplicateCategoryName = (categoryName: string) => {
    const copyName = uniqueCategoryName(`Copy ${categoryName}`);
    setCategoryNames(`${categoryNames},${copyName}`);
    let catItems = items.map((i) => ({ ...i }));
    catItems = catItems.filter((i) => i.category === categoryName);
    catItems.forEach((i) => {
      i.category = copyName;
      i.id = v4();
    });
    const newItems = [...items, ...catItems];
    setItems(newItems);
  };

  const categoryDelete = () => {
    catReplace(activeCategory, '');
    catItemsUpdateCategoryName(activeCategory, 'Uncategorized');
  };

  const moveCategory = (catName: string, moveUp: boolean) => {
    const categories = categoryNames.split(',');
    const i = categories.findIndex((c) => c === catName);
    const dir = moveUp ? -1 : 1;
    if (i === 0 && moveUp) return;
    if (i === categories.length - 1 && !moveUp) return;
    const newCategories = categories.filter((c) => c !== catName);
    newCategories.splice(i + dir, 0, catName);
    setCategoryNames(newCategories.join(','));
  };

  const categoryButtonHandler = (action: string,
    categoryName: string,
    e: MouseEvent<HTMLButtonElement>) => {
    setActiveCategory(categoryName);
    switch (action) {
      case 'REMOVE':
        alertCatOpen.onOpen();
        break;
      case 'ADDITEM':
        onAddOpen();
        break;
      case 'DUPLICATE':
        duplicateCategoryName(categoryName);
        break;
      case 'MOVEUP':
        moveCategory(categoryName, true);
        break;
      case 'MOVEDN':
        moveCategory(categoryName, false);
        break;
      case 'EDIT':
        setEditCategoryName(categoryName);
        setCategoryEditMode(true);
        break;
      case 'EDITCOMPLETE':
        if (categoryName.length !== 0) {
          // item 553 — never let a rename collide with ANOTHER category (that reintroduces
          // the name-collision data-loss bug); suffix it unique against the others.
          {
            const finalName = uniqueCategoryName(categoryName, activeCategory);
            catReplace(activeCategory, finalName);
            catItemsUpdateCategoryName(activeCategory, finalName);
          }
          setCategoryEditMode(false);
        }
        break;
      default:
        break;
    }
    e.preventDefault();
  };

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      mb={{ base: -4, md: -8 }}
      px={{ base: 4, md: 10 }}
      pt={{ base: 4, md: 6 }}
      pb={{ base: 6, md: 8 }}
      minH="calc(100vh - 160px)"
    >
      <AuroraBackground />
      {/* item 353c — sticky doc-form title, same treatment as the entity forms: pinned at
          its natural spot below the dashboard (gap preserved), form scrolls under it.
          (293's flat-black-box revert is superseded — the backing is glass now.) */}
      <Box position="relative" zIndex={1}>
        <FormBreadcrumbs crumbs={crumbs} />
      </Box>
      <PageHeader
        sticky
        compact
        icon={<BrandIcon src={icon} color="currentColor" boxSize="18px" />}
        title={`${id !== NIL ? 'EDIT' : 'ADD'} ${title}`}
        actions={(
          <IconButton
            variant="ghost"
            aria-label="back"
            onClick={closeForm}
            icon={<CloseIcon />}
          />
        )}
        // item 647 — RNWY guided flow: the step-progress bar pinned in the sticky header
        belowBar={stepProgress ? stepProgress(liveValues, goToStep) : undefined}
      />
      <Box
        {...glass}
        position="relative"
        zIndex={1}
        overflow="hidden"
        p={{ base: 4, md: 6 }}
      >
        {aurora && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="2px"
            bgGradient="linear(to-r, transparent, brandGreen.400, transparent)"
            boxShadow="0 0 14px var(--chakra-colors-brandGreen-400)"
          />
        )}
        <Form
          onSubmit={onSubmit}
          validate={validate}
          validateOnBlur
          // item 555 — SAVE-RACE FIX (part 2): a post-save / background refetch changes `doc`,
          // which re-runs initialValues. Without this, react-final-form resets EVERY field to the
          // refetched value — clobbering fields the user is mid-edit (terms, title, dates). Keeping
          // dirty fields means only untouched fields adopt the new server value.
          keepDirtyOnReinitialize
          initialValues={{
            ...doc, ...taxStatePrefill, ...projectPrefill, ...datePrefill, ...customerPrefill, ...kindPrefill,
          }}
          render={({ handleSubmit, submitting, form }) => (
            <form
              onSubmit={async (e) => {
                await handleSubmit(e);
                // dart — if validation blocked the submit, TELL the user what's missing instead
                // of the button appearing to do nothing (the invalid field may be off-screen).
                const s = form.getState();
                if (s.hasValidationErrors && s.errors && Object.keys(s.errors).length) {
                  toast({
                    title: "Can't save yet — required fields missing",
                    description: `Please complete: ${Object.keys(s.errors).map(humanizeField).join(', ')}`,
                    status: 'warning',
                    duration: 9000,
                    isClosable: true,
                  });
                }
              }}
            >
              <FormSpy
                subscription={{ dirty: true, values: true }}
                onChange={(state) => {
                  dirtyRef.current = state.dirty;
                  latestValuesRef.current = state.values as Record<string, unknown>;
                  persistDraft();
                  scheduleAutoSave();
                }}
              />
              <DraftValueRestorer
                form={form}
                values={restoredValues}
                ready={draftLoaded && (isNewDoc || doc !== undefined)}
              />
              {/* item 441 — approved/finalized/paid/closed/void ribbon */}
              {statusStamp}
              {/* item 874 (Chad) — the LOCK banner: unmissable, and says what to do instead */}
              {readOnlyLock && (
                <Flex
                  align="center"
                  gridGap={3}
                  p={4}
                  mb={4}
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="orange.400"
                  bg="rgba(237,137,54,0.10)"
                >
                  <Box color="orange.400" fontSize="xl"><FaLock /></Box>
                  <Text fontSize="sm" fontWeight="semibold">{readOnlyNotice ?? 'This document is locked and can no longer be edited.'}</Text>
                </Flex>
              )}
              {/* item 213 — another user holds the edit lock: read-only + Take Over */}
              {lockedByOther && (
                <EditLockBanner holderName={lockHolder} onTakeOver={lockTakeOver} />
              )}
              {draftRestored && (
                <Flex
                  align="center"
                  gap={3}
                  mb={4}
                  px={4}
                  py={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={draftBannerBorder}
                  bg={draftBannerBg}
                >
                  <Box color="orange.400" fontSize="lg">
                    <FaHistory />
                  </Box>
                  <Text fontSize="sm" flex="1">
                    We restored unsaved changes from your last visit. Save them, or discard to
                    go back to the saved version.
                  </Text>
                  <Button size="sm" variant="outline" onClick={() => discardDraft(form)}>
                    Discard draft
                  </Button>
                </Flex>
              )}
              {/* item 647 — RNWY guided flow: lift live values up so the sticky header's progress
                  bar can read them (renders null; RNWY only). */}
              {stepProgress && <HeaderValueLift onValues={setLiveValues} />}
              {/* item 668 — collapse/expand-all for the whole step group (RNWY guided flow)
                  item 683 (Chad) — + the personal "Auto-collapse steps" toggle */}
              {stepMode && (
                <Flex justify="space-between" align="center" mb={2}>
                  <Flex align="center">
                    <Switch
                      size="sm"
                      mr={2}
                      id="rnwy-auto-collapse"
                      isChecked={autoCollapse}
                      onChange={(e) => toggleAutoCollapse(e.target.checked)}
                    />
                    <Text as="label" htmlFor="rnwy-auto-collapse" fontSize="xs" cursor="pointer">
                      Auto-collapse steps
                    </Text>
                  </Flex>
                  {/* item 702 (Chad) — standardized expand/collapse-all control (was a plain ghost button) */}
                  <ExpandCollapseAllButton
                    size="xs"
                    allOpen={openSteps.length >= stepCount}
                    onToggle={() => (openSteps.length >= stepCount ? collapseAll() : allOpen())}
                  />
                </Flex>
              )}
              {/* item 841c (Chad) — non-step forms open Information AND the Items Section by default
                  (items auto-expanded; still collapsible via defaultIndex=[0,1] below). */}
              {/* item 874 (Chad) — the HARD lock: every control inside the accordion PANELS
                  goes inert + dimmed (inputs, selects, toggles, item-row menus), while the
                  section headers (browse) and the footer action bar (Void/PDF/Send/Cancel —
                  they sit outside the panels) stay fully live. */}
              <Box
                sx={readOnlyLock ? {
                  '.chakra-accordion__panel input, .chakra-accordion__panel textarea, .chakra-accordion__panel select, .chakra-accordion__panel button, .chakra-accordion__panel label, .chakra-accordion__panel [role="group"]':
                    { pointerEvents: 'none' },
                  '.chakra-accordion__panel': { opacity: 0.75 },
                } : undefined}
              >
                <Accordion
                  index={stepMode ? openSteps : undefined}
                  onChange={stepMode ? (i) => {
                    const next = Array.isArray(i) ? i : [i];
                    // item 683 — auto-collapse: opening a new step keeps ONLY it open (others collapse).
                    // Collapsing (shorter array) passes through so you can still close a step.
                    if (autoCollapse && next.length > 1) {
                      const added = next.find((x) => !openSteps.includes(x));
                      setOpenSteps(added !== undefined ? [added] : next);
                    } else {
                      setOpenSteps(next);
                    }
                  } : undefined}
                  defaultIndex={stepMode ? undefined : [0, 1]}
                  allowMultiple
                  allowToggle
                >
                  {/* item 647 — RNWY guided flow: render the ordered Information step-cards. Every
                    OTHER form (standard proposal + all non-proposal docs) has no infoSteps and
                    falls through to the single, unchanged "Information Section" accordion. */}
                  {infoSteps && infoSteps.length ? (
                    infoSteps.map((step, stepIndex) => (
                    // item 710 (Chad) — id + scrollMarginTop so the clickable progress bar can jump here
                      <AccordionItem {...stepCardProps} key={step.title} id={`rnwy-step-${stepIndex}`} sx={{ scrollMarginTop: '150px' }}>
                        <AccordionButton w="auto" borderRadius="md" {...stepBtnProps}>
                          <Box pr={4} fontSize="md">
                            <FaInfoCircle />
                          </Box>
                          <Heading size="sm">{step.title}</Heading>
                          <AccordionIcon fontSize="sm" />
                        </AccordionButton>
                        <AccordionPanel px={0}>
                          {step.content}
                          <Text mt={4} textAlign="center" fontSize="xs" color={infoFooterColor}>
                            Go to the next step below ↓
                          </Text>
                        </AccordionPanel>
                      </AccordionItem>
                    ))
                  ) : (
                    <AccordionItem {...stepCardProps}>
                      {/* item 553 — the toggle is only as wide as the icon+label+chevron (was the
                        full bar), so a stray click in the empty header space no longer collapses
                        the section. */}
                      <AccordionButton w="auto" borderRadius="md" {...stepBtnProps}>
                        <Box pr={4} fontSize="md">
                          <FaInfoCircle />
                        </Box>
                        <Heading size="sm">{stepMode ? 'Step 1' : 'Information Section'}</Heading>
                        <AccordionIcon fontSize="sm" />
                      </AccordionButton>
                      <AccordionPanel px={0}>
                        {infoSection}
                        {stepMode && (
                        <Text mt={4} textAlign="center" fontSize="xs" color={infoFooterColor}>
                          Go to the next step below ↓
                        </Text>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {/* item 670 — RNWY drops this whole section (no line items / discount / tax here) */}
                  {!hideItemsSection && (
                  <AccordionItem {...stepCardProps}>
                    <AccordionButton w="auto" borderRadius="md" {...stepBtnProps}>
                      <Box pr={4}>
                        <FaGripLines fontSize="sm" />
                      </Box>
                      {/* item 647 — RNWY guided flow labels the items step "Items"; the old 2-step
                        mode used "Step 2"; every other form keeps "Items Section". */}
                      <Heading size="sm">{itemsSectionTitle}</Heading>
                      <AccordionIcon fontSize="sm" />
                    </AccordionButton>
                    <AccordionPanel px={0}>
                      {/* item 654 — RNWY (hideItemsGrid) is engine-priced: no standalone line-items
                        grid (one-off items live in Step 2 "One-time add-ons"). Keep discount/tax. */}
                      {!hideItemsGrid && (
                      <FormRow
                        heading="Items"
                        subHeading="Includes all the items organized by categories"
                      >
                        <Flex
                          as={Button}
                          w="100%"
                          fontWeight="normal"
                          fontSize="sm"
                          align="center"
                          justify="start"
                          variant="ghost"
                          onClick={() => categoryAdd()}
                        >
                          Add A New Category
                        </Flex>
                        <ItemsElement
                          categoryNames={categoryNames}
                          items={items}
                          setItems={setItems}
                          categoryButtonHandler={categoryButtonHandler}
                          activeCategory={activeCategory}
                          categoryEditMode={categoryEditMode}
                          editCategoryName={editCategoryName}
                          setEditCategoryName={setEditCategoryName}
                          onAddClose={onAddClose}
                          isAddOpen={isAddOpen}
                        />
                      </FormRow>
                      )}
                      {afterItems}
                    </AccordionPanel>
                  </AccordionItem>
                  )}
                  <Field name="id" component={TextInputHidden} value={doc ? (doc as any).id : NIL} />
                  {/* item 577 (was 242) — the unified action bar, now three semantic clusters
                    with deliberate wrapping: primary verbs first, utilities wrap as a group,
                    and the save cluster (badge + Cancel + Save) anchors right and never
                    separates. All one size (sm). */}
                  <FormActionBar
                    primary={footerPrimary}
                    utilities={footerActions}
                    save={(
                      <>
                        {!hideSave && <AutoSaveBadge state={saveBadge} isNew={isNewDoc} />}
                        <Button size="sm" variant="ghost" onClick={closeForm}>Cancel</Button>
                        {/* item 534 — gray the Save out when there's nothing to save (existing
                          docs whose on-screen state matches what's saved). New docs stay
                          saveable. */}
                        {!hideSave && (
                        <Button
                          size="sm"
                          type="submit"
                          flexShrink={0}
                          disabled={submitting || lockedByOther
                            || (autoSaveEnabled && (saveBadge === 'clean' || saveBadge === 'saved'))}
                        >
                          {saveLabel}
                        </Button>
                        )}
                      </>
                  )}
                  />
                </Accordion>
              </Box>
              <Portal>
                <Alert
                  ref={(disclosure: any) => (alertCatOpen = disclosure)}
                  onConfirm={categoryDelete}
                  message="Are you sure you would like to remove this category?"
                />
              </Portal>
            </form>
          )}
        />
      </Box>
    </Box>
  );
};

export default DocumentFormScaffold;
